import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ViewLevels from "./viewLevels";
import ViewIntakes from "./ViewIntakes";
import ProviderCategories from "./ProviderCategories";
import {
  PROVIDER_SETTINGS_TABS,
  resolveProviderSettingsTab,
} from "./providerSettingsConstants";
import "../Clients/clients.css";
import "../reports/commissions/commissions-responsive.css";
import "../../styles/tailwind.css";

function ProviderSettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = useMemo(() => {
    try {
      return getPermissions() || {};
    } catch {
      return {};
    }
  }, []);

  const resolvedTab = resolveProviderSettingsTab(
    location.pathname,
    searchParams,
  );
  const [activeTab, setActiveTab] = useState(resolvedTab);

  useEffect(() => {
    setActiveTab(resolvedTab);
  }, [resolvedTab]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    if (location.pathname === "/providers/settings") {
      setSearchParams({ tab: tabId }, { replace: true });
      return;
    }

    const nextPath =
      tabId === "levels"
        ? "/providers/view-levels"
        : tabId === "intakes"
          ? "/providers/view-intakes"
          : "/providers/provider-categories";

    if (location.pathname !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  };

  if (permissions.providers !== 1) {
    return (
      <div className="client-invoices-xero px-3 sm:px-4 lg:px-5">
        <div className="client-invoices-xero__page-head">
          <h1 className="client-invoices-xero__title">Provider Settings</h1>
          <p className="client-invoices-xero__subtitle">
            You do not have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="client-invoices-xero px-3 sm:px-4 lg:px-5">
      <div className="client-invoices-xero__breadcrumb">
        <span>Manage Providers</span>
        <span aria-hidden="true">›</span>
        <span>Settings</span>
      </div>

      <div className="client-invoices-xero__page-head">
        <h1 className="client-invoices-xero__title">Provider Settings</h1>
        <p className="client-invoices-xero__subtitle">
          Manage course levels, intakes, and provider categories in one place.
        </p>
      </div>

      <nav
        className="mb-[18px] grid grid-cols-1 gap-1.5 overflow-hidden rounded-xl border border-[#dbe3ec] bg-[#eef2f7] p-1.5 shadow-[inset_0_1px_2px_rgba(15,23,42,0.04)] md:grid-cols-3"
        role="tablist"
        aria-label="Provider settings sections"
      >
        {PROVIDER_SETTINGS_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`provider-settings-panel-${tab.id}`}
              id={`provider-settings-tab-${tab.id}`}
              className={`client-invoices-xero__module-switch-item${
                isActive ? " is-active" : ""
              }`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="client-invoices-xero__module-switch-icon">
                <Icon aria-hidden="true" />
              </span>
              <span className="client-invoices-xero__module-switch-copy">
                <span className="client-invoices-xero__module-switch-label">
                  {tab.label}
                </span>
                <span className="client-invoices-xero__module-switch-subtitle">
                  {tab.subtitle}
                </span>
              </span>
            </button>
          );
        })}
      </nav>

      <div
        className="mt-1"
        role="tabpanel"
        id={`provider-settings-panel-${activeTab}`}
        aria-labelledby={`provider-settings-tab-${activeTab}`}
      >
        {activeTab === "levels" ? <ViewLevels embedded /> : null}
        {activeTab === "intakes" ? <ViewIntakes embedded /> : null}
        {activeTab === "categories" ? <ProviderCategories embedded /> : null}
      </div>
    </div>
  );
}

export default ProviderSettings;
